import { useState } from "react";
import { Download, FileDown, AlertTriangle } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useToast } from "@/hooks/useToast";
import {
  downloadOrgExport,
  downloadPayrollCsv,
  type PayrollFormat,
} from "@/api/exports";

const FORMATS: { value: PayrollFormat; label: string }[] = [
  { value: "adp", label: "ADP Workforce Now / Run" },
  { value: "gusto", label: "Gusto" },
  { value: "paychex", label: "Paychex Flex" },
];

/**
 * Org-wide bulk-export controls. Two operations live here:
 *   1. Payroll CSV — pick a vendor + pay period, get a vendor-flavoured CSV.
 *   2. Full org JSON export — for customers leaving the platform.
 *
 * Both endpoints are gated server-side; this UI is rendered only inside the
 * SUPER_ADMIN-only Settings tab so HR Admins won't see it at all.
 */
export function ExportsSettings() {
  const { addToast } = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const fortnightAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

  const [fmt, setFmt] = useState<PayrollFormat>("adp");
  const [periodStart, setPeriodStart] = useState(fortnightAgo);
  const [periodEnd, setPeriodEnd] = useState(today);
  const [downloadingPayroll, setDownloadingPayroll] = useState(false);
  const [downloadingOrg, setDownloadingOrg] = useState(false);

  const onPayroll = async () => {
    setDownloadingPayroll(true);
    try {
      await downloadPayrollCsv(fmt, periodStart, periodEnd);
      addToast("success", "Payroll CSV downloaded");
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Export failed");
    } finally {
      setDownloadingPayroll(false);
    }
  };

  const onOrgExport = async () => {
    setDownloadingOrg(true);
    try {
      await downloadOrgExport();
      addToast("success", "Organization export downloaded");
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Org export failed");
    } finally {
      setDownloadingOrg(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileDown className="h-5 w-5" />
            Payroll CSV export
          </CardTitle>
        </CardHeader>
        <p className="text-sm text-text-muted mb-4">
          Generate a payroll-period CSV in your provider's import format. Each
          download writes an audit row.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium text-text-muted">Format</label>
            <select
              value={fmt}
              onChange={(e) => setFmt(e.target.value as PayrollFormat)}
              className="w-full mt-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              {FORMATS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Period start"
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
          />
          <Input
            label="Period end"
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
          />
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={onPayroll} isLoading={downloadingPayroll}>
            <Download className="h-4 w-4 mr-1" /> Download CSV
          </Button>
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Full organization export
          </CardTitle>
        </CardHeader>
        <p className="text-sm text-text-muted mb-4">
          Downloads a single JSON file containing every record tied to your
          organization — employees, leave, time, documents, invoices, audit
          history. Intended for customers leaving the platform or for periodic
          backups. Large orgs may take a moment.
        </p>
        <div className="flex justify-end">
          <Button
            variant="secondary"
            onClick={onOrgExport}
            isLoading={downloadingOrg}
          >
            <Download className="h-4 w-4 mr-1" /> Download org JSON
          </Button>
        </div>
      </Card>
    </div>
  );
}
